import React from 'react';
import { Network, Cpu, Trophy, Clock } from 'lucide-react';

const reasons = [
  {
    icon: Network,
    title: 'Extensive Network & Reach',
    description:
      '1,500+ affiliate platforms amplify your content, reaching 1 million monthly visits, broadening exposure and connecting with diverse B2B decision-makers.',
    stat: '1,500+',
    statLabel: 'Platforms',
  },
  {
    icon: Cpu,
    title: 'Exclusive Tech Platforms',
    description:
      'Utilize our proprietary platforms like Whitepapers Online and ToolsMetric, employing advanced tech and data for optimized content syndication and precise targeting.',
    stat: '2',
    statLabel: 'Proprietary Platforms',
  },
  {
    icon: Trophy,
    title: 'Expertise & Experience',
    description:
      'iTMunch excels as a B2B content syndication service provider, partnering with 90+ global brands and adapting strategies for effective content distribution.',
    stat: '90+',
    statLabel: 'Global Brands',
  },
  {
    icon: Clock,
    title: 'Efficiency & Turnaround',
    description:
      'We prioritize efficiency with a 24-hour turnaround for content syndication services, valuing your time and delivering rapid results in the fast-paced business world.',
    stat: '24h',
    statLabel: 'Turnaround Time',
  },
];

export default function WhyChooseSection() {
  return (
    <section
      id="why-us"
      data-testid="why-choose-section"
      className="py-20 md:py-32 bg-slate-50"
      aria-label="Why Choose iTMunch"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Image + overlay */}
          <div className="relative order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Business team collaborating on B2B content syndication strategy"
                className="w-full h-auto object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
            {/* Floating stat card */}
            <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-white rounded-xl shadow-lg shadow-slate-200/60 border border-slate-100 p-4 md:p-5">
              <p className="font-['Outfit'] text-2xl md:text-3xl font-bold text-[#4AC5DB]">1M+</p>
              <p className="text-xs md:text-sm text-slate-500 font-medium">Monthly Reach</p>
            </div>
          </div>

          {/* Right - Content */}
          <div className="order-1 lg:order-2">
            <span className="inline-block text-sm font-semibold tracking-wide uppercase text-[#4AC5DB] mb-4">
              Why iTMunch
            </span>
            <h2
              data-testid="why-choose-heading"
              className="font-['Outfit'] text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight mb-4"
            >
              Why Choose iTMunch as Your Content Syndication Vendor?
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-10">
              Unmatched expertise and a results-driven approach to B2B content syndication
            </p>

            <div className="space-y-6">
              {reasons.map((reason, i) => {
                const Icon = reason.icon;
                return (
                  <div
                    key={reason.title}
                    data-testid={`why-card-${i}`}
                    className="flex gap-4 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white border border-slate-100 group-hover:border-[#4AC5DB]/30 flex items-center justify-center shrink-0 transition-colors shadow-sm">
                      <Icon className="w-6 h-6 text-[#4AC5DB]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-1">
                        {reason.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
